import React, { useMemo, useEffect, useState } from 'react';
import { Typography, Divider, Collapse, Badge } from 'antd';
import { Email, Information } from '@carbon/icons-react';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { DATE_TIME_FORMAT_SENTENCE, TIMEZONE_EST } from 'components/AriaDatePicker/AriaDatePicker';
import { ENDASH } from 'constants';
import Card from 'components/Card/Card';
import { useAsync } from 'react-async-hook';
import legacyService from 'services/legacy.service';
import Html from '../../../../../../Html';
import { checklistItemsAtom } from '../../../atoms';

const { Text } = Typography;

export default function MessagesSection({ application, loading: itemsLoading }) {
  const [items] = useAtom(checklistItemsAtom);
  const [isCardOpen, setIsCardOpen] = useState(false);

  const { result: messages = [], loading } = useAsync(async () => {
    if (!application?.id) {
      return [];
    }
    return await legacyService.getChecklistMessages(application.id);
  }, [application?.id]);

  const noActionItems = useMemo(
    () => items?.filter((item) => item.actionType === 'NoAction' && item.hide !== true) || [],
    [items],
  );

  const totalCount = messages.length;

  // Get localStorage key for this application
  const storageKey = `ccaoa__messages_last_seen_${application?.id}`;

  // Get last seen message date from localStorage
  const lastSeenDate = useMemo(() => {
    const stored = localStorage.getItem(storageKey);
    return stored || null;
  }, [storageKey]);

  // Find the latest message date
  const latestMessageDate = useMemo(() => {
    if (messages.length === 0) return null;
    const sorted = [...messages].sort((a, b) => {
      if (!a.dateSent) return 1;
      if (!b.dateSent) return -1;
      return dayjs(b.dateSent).valueOf() - dayjs(a.dateSent).valueOf();
    });
    return sorted[0]?.dateSent;
  }, [messages]);

  // Check if there are new messages
  const hasNewMessages = useMemo(() => {
    if (!latestMessageDate) return false;
    if (!lastSeenDate) return messages.length > 0;
    return dayjs(latestMessageDate).isAfter(dayjs(lastSeenDate));
  }, [latestMessageDate, lastSeenDate, messages.length]);

  // Update localStorage when card is opened
  useEffect(() => {
    if (isCardOpen && latestMessageDate) {
      localStorage.setItem(storageKey, latestMessageDate);
    }
  }, [isCardOpen, latestMessageDate, storageKey]);

  // Helper to check if a message is new
  const isMessageNew = (msg) => {
    if (!msg.dateSent || !lastSeenDate) return true;
    return dayjs(msg.dateSent).isAfter(dayjs(lastSeenDate));
  };

  return (
    <div className="mt-8 mb-6">
      {/* No Action Items Section */}
      {!itemsLoading && noActionItems.length > 0 && (
        <div className="mt-12">
          {noActionItems.map((item, index) => (
            <Collapse
              key={index}
              className="mb-4 border border-gray-300 rounded-lg"
              ghost={false}
              expandIconPosition="end"
              items={[
                {
                  key: '1',
                  label: (
                    <div className="flex items-center gap-3">
                      <Information size={20} className="text-gray-600" />

                      <div>
                        <Text className="font-semibold text-gray-800">{item.category}</Text>

                        {item.lastModifiedDate && (
                          <Text className="block text-gray-500 text-xs">
                            As of {dayjs(item.lastModifiedDate).format(DATE_TIME_FORMAT_SENTENCE)}
                          </Text>
                        )}
                      </div>
                    </div>
                  ),
                  children: item.instructions ? (
                    <div className="text-gray-700">
                      <Html html={item.instructions} />
                    </div>
                  ) : null,
                },
              ]}
            />
          ))}
        </div>
      )}

      {/* Messages Section */}
      {messages?.length !== 0 && (
        <Card
          className="my-6 border-gray-300 shadow-sm mt-12"
          collapsible={true}
          defaultOpen={false}
          onOpenChange={setIsCardOpen}
          title={
            <div className="flex items-center gap-3">
              <Badge dot={hasNewMessages} offset={[0, 3]}>
                <Email className="text-blue-500" size={20} />
              </Badge>
              Messages {!loading && `(${totalCount})`}
            </div>
          }
        >
          <div className="space-y-5">
            {messages.map((msg, index) => {
              const isNew = isMessageNew(msg);
              return (
                <React.Fragment key={index}>
                  <div
                    className={`py-6 border-l-4 border-solid ${
                      isNew
                        ? 'border-l-blue-300 bg-blue-100 bg-opacity-30'
                        : 'border-l-gray-300 bg-gray-100 bg-opacity-30'
                    }`}
                  >
                    <div className="pl-4">
                      <Text className="block text-black font-bold">{msg.subject}</Text>

                      <Text className="block text-gray-500 text-sm mb-1">
                        {msg.dateSent ? dayjs(msg.dateSent).tz(TIMEZONE_EST).format(DATE_TIME_FORMAT_SENTENCE) : ENDASH}
                      </Text>
                      <Text className="block text-gray-900 mt-6">
                        <Html html={msg.body} />
                      </Text>
                    </div>
                  </div>

                  {index < messages.length - 1 && <Divider className="!my-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
